// Inspiration: https://medium.com/@rafaelogic/creating-a-custom-rule-in-vue-vee-validate-83777b9a5126
import { extend, localize, setInteractionMode, configure } from 'vee-validate';

import en from 'vee-validate/dist/locale/en.json';
import fr from 'vee-validate/dist/locale/fr.json';
import * as rules from 'vee-validate/dist/rules';
import i18n from '@/js/i18n';
import compteRules, { maxNumeroCivicLength, minRueLength } from './rules/compte';

import { getAge } from './rules/date_compare';
import { splitAddress } from './rules/split_adresse';
import { isValidDate } from '@/js/validators/utils';
import { parseFloatValueFromCurrency, formatMontant } from '@/helpers/StringHelpers';

// install rules and localization
Object.keys(rules).forEach((rule) => {
  extend(rule, rules[rule]);
});

localize({ fr, en });

// Validation du input en sortant du champ
setInteractionMode('eager');

// extend("is_earlier", {
//  validate: (value, { compare }) => {
//   return dateCompare({ value, compare, validationType: "earlier" });
//  },
//  params: ["compare", "dateType"],
//  message: "The selected date must not be earlier than {dateType}",
// });

// extend("is_beyond", {
//  validate: (value, { compare }) => {
//    return dateCompare({ value, compare, validationType: "beyond" });
//  },
//  params: ["compare", "dateType"],
//  message: "The selected date must not be older than {dateType}",
// });

const CS_AGE_MINUMUM = 18;
const CS_AGE_MAXIMUM = 108;

// "i" permet de faire la gestion des lettres en case insensitive et vérifier toutes les possibilités
const casePostale = new RegExp(
  '((b|c)[-. ]?\\s?p[. ]?\\s|case\\spostale\\s|boî?te\\spostale\\s|p[. ]?o.[. ]?(b|box)?\\s|bin\\s).*',
  'i'
);

export const maxRueLength = 40;

extend('is_age_requis', {
  validate: (value) => {
    let age = getAge(value);
    if (!age) {
      // Ne devrait pas arriver sur is_valid_date est utilisé
      return i18n.t('validations.messages.dateNaissanceInvalide');
    }

    if (age < CS_AGE_MINUMUM) {
      return i18n.t('validations.messages.ageMinimum');
    }

    // On doit accepter l'âge maximal le jour même de l'anniversaire
    // Ex: 108 ans + 0 jour: Ok
    //     108 ans + 1 jour: Refusé
    age = getAge(value, 1);
    if (age >= CS_AGE_MAXIMUM) {
      return i18n.t('validations.messages.ageMaximum');
    }
    return true;
  },
});

extend('is_valid_date', {
  validate: (value) => {
    if (isValidDate(value)) {
      return true;
    }
    if (value.length < i18n.t('compte.dateDeNaissance.formatAffichage').length) {
      return i18n.t('validations.messages.dateNaissanceLongueurInvalide');
    }
    return i18n.t('validations.messages.dateNaissanceInvalide');
  },
});

extend('is_valid_address', {
  validate: (value) => {
    const regexNumero = new RegExp('^0+$');

    const splitAdresse = splitAddress(value);
    const numero = splitAdresse.number;
    const rue = splitAdresse.street.trim();

    if (numero.length > maxNumeroCivicLength || rue.length > maxRueLength) {
      return i18n.t('compte.adresse.erreurMessageFormatInvalide');
    }
    if (
      numero === '' ||
      numero.match(regexNumero) ||
      splitAdresse.space === '' ||
      rue.length < minRueLength
    ) {
      return i18n.t('compte.adresse.erreurMessageAdresseInvalide');
    }
    return true;
  },
});

extend('sans_case_postale', {
  validate: (value) => {
    if (value.match(casePostale)) {
      return i18n.t('compte.adresse.erreurMessageCasePostale');
    }

    return true;
  },
});

extend('min', {
  message: (field, { length }) => {
    return i18n.t('validations.messages.min', { minLength: length });
  },
});

extend('max', {
  message: (field, { length }) => {
    return i18n.t('validations.messages.max', { maxLength: length });
  },
});

extend('is_telephone_valid', {
  validate: (value) => {
    if (value.length < i18n.t('compte.telephone.mask').length) {
      return i18n.t('validations.messages.telephoneInvalide');
    }
    return true;
  },
});

extend('is_coupon_argent_web_valid', {
  validate: (value) => {
    if (value.length < i18n.t('compte.codeArgentWeb.mask').length) {
      return i18n.t('validations.messages.couponArgentWebInvalide');
    }
    return true;
  },
});

extend('is_code_promo_valid', {
  validate: (value) => {
    const regexAlphaNumSansEspace = new RegExp('^[a-zA-Z0-9]{1,16}$');
    if (value.match(regexAlphaNumSansEspace)) {
      return true;
    }
    return i18n.t('validations.messages.codePromoInvalide');
  },
});

extend('is_code_argent_web_valid', {
  validate: (value) => {
    const regexCouponArgentWeb = new RegExp('(1|8|9)', 'i');
    const maskOrigin = i18n.t('compte.codeArgentWeb.mask');
    const mask = maskOrigin.replace(new RegExp(/-/, 'g'), '');
    const minLength = mask.length;
    if (value.length < minLength) {
      return i18n.t('validations.messages.couponArgentWebInvalide');
    }
    if (value.match(regexCouponArgentWeb)) {
      return i18n.t('compte.codeArgentWeb.erreurMessage');
    }
    return true;
  },
});

extend('compteValidation', (value, args) => {
  let isValid = false;
  let messageErreur = '';
  const type = args[0];
  value = value.toString();
  compteRules.rules[type].forEach((el) => {
    if (!compteRules.validateCompteCondition(value, el.regex)) {
      messageErreur = el.messageErreur;
    } else {
      isValid = true;
    }
  });
  if (messageErreur != '') {
    return messageErreur;
  }
  return isValid;
});

extend('monetaire', {
  validate: (value) => {
    const montant = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(montant)) {
      // TODO: Manque traduction
      return false;
    }
    return true;
  },
});

extend('is_limite_depot_valid', {
  validate: (value, args) => {
    const minimumAutoriseDepotHebdo = parseFloat(args[0]);
    const maximumAutoriseDepotHebdo = parseFloat(args[1]);

    if (!minimumAutoriseDepotHebdo || !maximumAutoriseDepotHebdo) {
      return false;
    }

    const limite = typeof value === 'string' ? parseFloatValueFromCurrency(value) : value;
    if (!limite || limite === 0) {
      return i18n.t('validations.messages.depot.limiteDepotNonRenseignee');
    }

    if (limite < minimumAutoriseDepotHebdo || limite > maximumAutoriseDepotHebdo) {
      return i18n.t('validations.messages.depot.limiteDepotInvalide', {
        minLimiteMaxDepotHebdo: minimumAutoriseDepotHebdo,
        maxlimiteMaxDepotHebdo: maximumAutoriseDepotHebdo,
      });
    }
    console.log(true);
    return true;
  },
});

extend('is_limite_depot_bienJouer_valid', {
  validate: (value, args) => {
    const minimum = parseFloat(args[0]);
    const maximum = parseFloat(args[1]);

    if (!minimum || !maximum) {
      return false;
    }

    const limite = typeof value === 'string' ? parseFloatValueFromCurrency(value) : value;
    if (!limite || limite === 0) {
      return i18n.t('validations.messages.encadrerJeux.limiteNonRenseignee');
    }

    if (limite < minimum || limite > maximum) {
      return i18n.t('validations.messages.encadrerJeux.limiteDepotInvalide', {
        min: formatMontant(minimum),
        max: formatMontant(maximum),
      });
    }

    return true;
  },
});

extend('is_limite_temps_bienJouer_valid', {
  validate: (value, args) => {
    const maximum = parseFloat(args[0]);

    if (!maximum) {
      return false;
    }

    const limite = typeof value === 'string' ? parseFloatValueFromCurrency(value) : value;
    if (!limite || limite === 0) {
      return i18n.t('validations.messages.encadrerJeux.limiteNonRenseignee');
    }

    if (limite > maximum) {
      return i18n.t('validations.messages.encadrerJeux.limiteTempsInvalide', {
        max: maximum,
      });
    }
    return true;
  },
});

extend('is_depot_valid', {
  validate: (value, args) => {
    const depotMinimum = parseFloat(args[0]);
    const depotMaximum = parseFloat(args[1]);
    const limiteHebdoRestant = parseFloat(args[1]);
    const regexMontant = new RegExp('^[1-9]{1}[0-9]*([.,]{0,1}[0-9]{1,2})?$');

    if (regexMontant.test(value) == false) {
      return i18n.t('validations.messages.depot.formatMontantDepotInvalide');
    }

    const montant = typeof value === 'string' ? parseFloatValueFromCurrency(value) : value;
    if (!montant || !depotMinimum || !depotMaximum) {
      return false;
    }
    if (montant === 0 || montant < depotMinimum || montant > depotMaximum) {
      return i18n.t('validations.messages.depot.montantDepotInvalide', {
        limiteMinDepot: depotMinimum,
        limiteMaxDepot: depotMaximum,
      });
    }
    if (montant > limiteHebdoRestant) {
      return i18n.t('validations.messages.depot.depasseLimiteHebdo', {
        limiteHebdoRestant,
      });
    }

    return true;
  },
});

configure({
  // Logique d'affichage de message par défaut
  defaultMessage: (field, values) => {
    if (i18n.te(`validations.messages.${values._rule_}`)) {
      return i18n.t(`validations.messages.${values._rule_}`, values);
    }
    return i18n.t('validations.messages.defaut', values);
  },
});
